import { cn } from '../lib/cn.js';
import { t } from '../i18n/index.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Field.js';
import { IconChevronDown, IconFilter, IconSearch, IconX } from '../ui/Icon.js';
import {
  Menu, MenuCheckboxItem, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger,
} from '../ui/Menu.js';
import type { TableView } from './table-view.js';

/**
 * The search box, filter menus and result count for a table.
 *
 * ── WHY THE COUNT IS NOT OPTIONAL ─────────────────────────────────────────
 *
 * "12 of 103" is the difference between a filter and a bug. A table showing
 * twelve rows with no statement about how many there were is indistinguishable
 * from a table that failed to load the rest — and in an accounting product,
 * quietly showing a subset of the ledger is the worst thing a list can do. So the
 * count renders whenever anything is filtering, and it says what was excluded
 * rather than only what survived.
 *
 * ── AND WHY "CLEAR" IS ALWAYS REACHABLE ───────────────────────────────────
 *
 * Every filtered state has one visible control that returns to everything. A
 * user who has narrowed to nothing needs a way out that is not reloading the
 * page, and the empty state alone cannot provide it — the empty state is inside
 * the table, and by then the reader is asking whether the data is gone.
 */
export function TableControls<Row>({
  view,
  className,
}: {
  view: TableView<Row>;
  className?: string;
}) {
  const filtering = view.activeFilterCount > 0;
  const searchable = view.searchPlaceholder !== undefined;

  return (
    <div className={cn('flex flex-1 flex-wrap items-center gap-2', className)}>
      {searchable && (
        <Input
          size="sm"
          className="w-full sm:w-64"
          value={view.search}
          onChange={(e) => view.onSearch(e.target.value)}
          placeholder={view.searchPlaceholder}
          aria-label={t('table.searchLabel')}
          leading={<IconSearch size={14} />}
        />
      )}

      {view.facets.map((facet) => (
        <Menu key={facet.id}>
          <MenuTrigger asChild>
            <Button
              size="sm"
              leading={<IconFilter size={14} />}
              trailing={<IconChevronDown size={14} />}
              // The count is on the trigger so a filtered table says which
              // filters are on without the menus being open.
              className={cn(facet.selectedCount > 0 && 'border-strong text-primary')}
            >
              {facet.label}
              {facet.selectedCount > 0 && (
                <Badge intent="info" className="ml-1">{facet.selectedCount}</Badge>
              )}
            </Button>
          </MenuTrigger>
          <MenuContent align="start" className="max-h-80 overflow-y-auto">
            <MenuLabel>{facet.label}</MenuLabel>
            {facet.options.length === 0 ? (
              <MenuItem disabled>{t('table.nothingToFilter')}</MenuItem>
            ) : (
              facet.options.map((option) => (
                // MenuCheckboxItem, not a checkbox hand-built inside MenuItem.
                // The first version stacked three spans into the item's
                // `flex-1 truncate` wrapper, where a grid child is block-level
                // and broke the row in two. It also announced as a plain menu
                // item rather than a checkbox.
                <MenuCheckboxItem
                  key={option.value}
                  checked={option.selected}
                  // Radix closes on select by default. Selecting three
                  // marketplaces would then be three trips back to the menu.
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() => facet.toggle(option.value)}
                  // The count is what makes a filter menu useful rather than a
                  // list of guesses: it says what each choice will leave you.
                  trailing={option.count.toLocaleString('en-IN')}
                >
                  {option.label}
                </MenuCheckboxItem>
              ))
            )}
            {facet.selectedCount > 0 && (
              <>
                <MenuSeparator />
                <MenuItem onSelect={() => facet.clear()}>
                  {t('table.clearFacet', { facet: facet.label.toLowerCase() })}
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>
      ))}

      {filtering && (
        <div className="ml-auto flex items-center gap-2">
          {/* One message, not a sentence assembled around a bolded span. A
              split sentence cannot be translated and cannot be reordered. */}
          <span className="tnum text-sm text-tertiary">
            {t('common.of', {
              shown: view.matchCount.toLocaleString('en-IN'),
              total: view.totalCount.toLocaleString('en-IN'),
            })}
          </span>
          <Button size="sm" variant="ghost" leading={<IconX size={14} />} onClick={view.clearAll}>
            {t('common.clear')}
          </Button>
        </div>
      )}
    </div>
  );
}
